import {useRef, useState, useCallback} from 'react';
import axios from 'axios';
import FFmpegService, {FFmpegError} from '@/services/media/FFmpegService';
import opfsService, {OPFSQuotaExceededError} from '@/services/media/OPFSService';
import {stripFileExtension, triggerDownload} from '@/utils/fileUtils';
import {VideoDimensions} from "@/Pages/Subtitle/Partials/VideoPlayer";
import * as Sentry from "@sentry/react";
import {getLanguageName} from "@/utils/textFormatting";

type UseSoftcodeExportOptions = {
    jobId: number;
    jobName: string;
    fileName: string;
    language: string;
    mediaFile: File;
    withStyles: boolean;
    videoDimensions: VideoDimensions | null;
    onSuccess: () => void;
};

type UseSoftcodeExportReturn = {
    progress: number | null;
    isExporting: boolean;
    handleExport: (options: UseSoftcodeExportOptions) => Promise<void>;
    handleCancel: () => void;
};

export default function useSoftcodeExport(): UseSoftcodeExportReturn {
    const [progress, setProgress] = useState<number | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const ffmpegRef = useRef<FFmpegService | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleExport = useCallback(async ({
        jobId,
        jobName,
        fileName,
        language,
        videoDimensions,
        mediaFile,
        onSuccess,
        withStyles
    }: UseSoftcodeExportOptions) => {

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsExporting(true);
        setProgress(0);

        const inputDir = '/input';
        const inputPath = `/input/${mediaFile.name}`;
        const opfsFileName = `softcode-${Date.now()}`;

        try {
            const format = withStyles ? 'ass' : 'srt';
            const subtitleResponse = await axios.get(
                route('job.download', {
                    job: jobId,
                    format,
                    language,
                    options: {},
                    videoDimensions
                })
            );
            const subtitleContent = subtitleResponse.data;
            const subtitleBuffer = new TextEncoder().encode(subtitleContent).buffer;

            // Proactively allocate space for the output (estimate 1.2x input size for safety)
            await opfsService.allocateSpace(
                Math.ceil(mediaFile.size * 1.2),
                jobName
            );

            if (!ffmpegRef.current) {
                ffmpegRef.current = new FFmpegService();
            }
            await ffmpegRef.current.load();

            await ffmpegRef.current.createDir(inputDir);
            // @ts-ignore - mount WORKERFS to avoid copying file to MEMFS
            await ffmpegRef.current.mount('WORKERFS', {files: [mediaFile]}, inputDir);

            await ffmpegRef.current.embedSubtitle(
                inputPath,
                subtitleBuffer,
                language,
                format,
                opfsFileName,
                (p) => setProgress(p),
                signal
            );

            const outputName = `${stripFileExtension(fileName)}.mkv`;
            const opfsFile = await opfsService.finalizeFile(opfsFileName);

            if(opfsFile.size === 0) {
                throw new Error('Failed to softcode export: empty file');
            }

            const url = URL.createObjectURL(opfsFile);
            triggerDownload(url, outputName);

            setTimeout(() => {
                opfsService.deleteFile(opfsFileName);
                URL.revokeObjectURL(url);
            }, 5000);

            onSuccess();
        } catch (error: any) {
            await opfsService.deleteFile(opfsFileName).catch(console.warn);

            if (!signal.aborted) {
                if(error instanceof FFmpegError) {
                    throw new Error("Subtitle embedding failed with exit code " + error.exitCode);
                } else {
                    throw error;
                }
            } else {
                ffmpegRef.current = null;
            }
        } finally {
            try {
                await ffmpegRef.current?.unmount(inputDir);
                await ffmpegRef.current?.deleteDir(inputDir);
            } catch (e) {}
            setIsExporting(false);
            setProgress(null);
            abortControllerRef.current = null;
        }
    }, []);

    const handleCancel = useCallback(() => {
        abortControllerRef.current?.abort();
    }, []);

    return {
        progress,
        isExporting,
        handleExport,
        handleCancel
    };
}
